/**
 * Import Dialog Component
 *
 * Shows a dialog when importing files with ambiguous context or metadata.
 * Allows users to specify file type, tags, and target location.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileText, FileCode, AlertCircle } from 'lucide-react';
import type { ImportOptions } from '@/data/import/ImportManager';
import type { DropContext } from '../FileDropZone';

interface ImportDialogProps {
  files: File[];
  context: DropContext;
  targetFolderId?: string;
  onImport: (
    options: ImportOptions & {
      fileType?: 'phrase' | 'document';
      tags?: string[];
    }
  ) => void;
  onCancel: () => void;
}

interface FileImportSettings {
  type: 'phrase' | 'document';
  tags: string[];
  overwrite: boolean;
}

export function ImportDialog({
  files,
  context,
  targetFolderId: _targetFolderId,
  onImport,
  onCancel,
}: ImportDialogProps) {
  const [fileSettings, setFileSettings] = useState<
    Record<string, FileImportSettings>
  >(() => {
    const settings: Record<string, FileImportSettings> = {};

    files.forEach((file) => {
      const extension = '.' + file.name.split('.').pop()?.toLowerCase();
      const isMarkdown = extension === '.md' || extension === '.mdx';

      settings[file.name] = {
        type: isMarkdown ? 'document' : 'phrase',
        tags: [],
        overwrite: false,
      };
    });

    return settings;
  });

  const [globalTags, setGlobalTags] = useState<string>('');
  const [applyGlobalSettings, setApplyGlobalSettings] = useState(
    files.length > 1
  );
  const [conflictResolution, setConflictResolution] =
    useState<ImportOptions['conflictResolution']>('skip');

  // Check if any files are .phrasewright format
  const hasPhrasewriteFiles = files.some((f) =>
    f.name.endsWith('.phrasewright')
  );

  // Handle import
  const handleImport = () => {
    const tags = globalTags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    const options: ImportOptions & {
      fileType?: 'phrase' | 'document';
      tags?: string[];
    } = {
      conflictResolution,
      validateContent: true,
      preserveIds: false,
    };

    if (applyGlobalSettings && files.length === 1) {
      // Single file with specific settings
      const settings = fileSettings[files[0].name];
      options.fileType = settings.type;
      options.tags = [...settings.tags, ...tags];
      options.overwriteExisting = settings.overwrite;
    } else if (applyGlobalSettings) {
      // Multiple files with global settings
      options.tags = tags;
    }

    onImport(options);
  };

  // Update file settings
  const updateFileSettings = (
    fileName: string,
    update: Partial<FileImportSettings>
  ) => {
    setFileSettings((prev) => ({
      ...prev,
      [fileName]: { ...prev[fileName], ...update },
    }));
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import Files</DialogTitle>
          <DialogDescription>
            Configure how these files should be imported into your project.
            {hasPhrasewriteFiles && (
              <div className="flex items-center gap-2 mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">
                  .phrasewright files detected - these will be imported with
                  their original structure
                </span>
              </div>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Files list */}
          <div>
            <Label className="text-sm font-medium mb-2">
              Files to import ({files.length})
            </Label>
            <ScrollArea className="h-48 border rounded-md p-3">
              <div className="space-y-2">
                {files.map((file) => {
                  const settings = fileSettings[file.name];
                  const extension =
                    '.' + file.name.split('.').pop()?.toLowerCase();
                  const isPhrasewright = extension === '.phrasewright';

                  return (
                    <div
                      key={file.name}
                      className="flex items-center justify-between p-2 rounded hover:bg-muted/50"
                    >
                      <div className="flex items-center gap-2 flex-1">
                        {settings.type === 'document' ? (
                          <FileText className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <FileCode className="w-4 h-4 text-muted-foreground" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {file.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                      </div>

                      {!isPhrasewright && files.length === 1 && (
                        <Select
                          value={settings.type}
                          onValueChange={(value: 'phrase' | 'document') =>
                            updateFileSettings(file.name, { type: value })
                          }
                        >
                          <SelectTrigger className="w-32 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="phrase">Phrase</SelectItem>
                            <SelectItem value="document">Document</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          {/* Global settings */}
          {files.length > 1 && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="apply-global"
                checked={applyGlobalSettings}
                onCheckedChange={(checked) =>
                  setApplyGlobalSettings(checked as boolean)
                }
              />
              <Label htmlFor="apply-global" className="text-sm">
                Apply settings to all files
              </Label>
            </div>
          )}

          {/* Tags input */}
          <div>
            <Label htmlFor="tags">Tags (comma-separated)</Label>
            <Input
              id="tags"
              placeholder="e.g., imported, documentation, api"
              value={globalTags}
              onChange={(e) => setGlobalTags(e.target.value)}
              className="mt-1"
            />
          </div>

          {/* Conflict resolution */}
          <div>
            <Label htmlFor="conflict">If file already exists</Label>
            <Select
              value={conflictResolution}
              onValueChange={(value) => setConflictResolution(value as 'skip' | 'overwrite' | 'rename' | 'merge')}
            >
              <SelectTrigger id="conflict" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="skip">Skip existing files</SelectItem>
                <SelectItem value="overwrite">
                  Overwrite existing files
                </SelectItem>
                <SelectItem value="rename">Rename imported files</SelectItem>
                <SelectItem value="merge">Merge content</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Import location info */}
          <div className="p-3 bg-muted/50 rounded-md">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium">Import location:</span>{' '}
              {context === 'EDITOR_NEW' && 'New file in editor'}
              {context === 'FOLDER_IMPORT' && `Selected folder`}
              {context === 'PROJECT_ROOT' && 'Project root'}
              {context === 'SIDEBAR' && 'File list'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleImport}>
            Import {files.length} {files.length === 1 ? 'file' : 'files'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
